/**
 * The two TSL node materials AD-012's propeller effect draws with, built
 * alongside `ramp-material.ts` under the same WebGPU renderer and the same
 * app-wide `ColorManagement.enabled = false` rule, so the colours below reach
 * the framebuffer as the `BARON.PAL` bytes they are read from.
 *
 * Both materials run on **one shared unit-radius disc geometry** and read the
 * same two per-instance attributes, so every propeller in the sky is one
 * instanced draw call per regime — two in total, which is AD-012 §5's budget.
 *
 * ## Two regimes, one crossfade
 *
 * `instanceRpm` (0..1, `flight/propeller-rpm.ts`) picks between them with a
 * `smoothstep` — the same cubic `discRegimeMix` computes on the CPU, so a test
 * can predict either material's opacity without a GPU:
 *
 * - **Running**: a flat wood-toned shimmer disc with a lighter tip arc and
 *   three faint rotating lobes of banding. There is no texture, no lighting
 *   term and no gradient beyond the tip mask — the tone is two palette entries
 *   and a mix between them.
 * - **Near-stopped**: `BLADE_COUNT` flat blade silhouettes, cut out of the
 *   same disc by an arc-distance test, at near-full alpha.
 *
 * ## Why the banding is not spun at the real rate
 *
 * See `flight/propeller-rpm.ts`'s `DISC_TURN_REV_PER_S`: a two-bladed pattern
 * turned at a rotary's true 20 rev/s and sampled at 60 Hz strobes, and that
 * strobe *is* the flicker AD-012 §2 rules out. The phase this material
 * receives is already integrated at the drawn rate, so the shader itself has
 * no notion of time at all and two frames at a fixed RPM differ by exactly the
 * rotation their `dt` earns.
 *
 * ## Alpha is a uniform, not a constant
 *
 * AD-012 §4 caps the cockpit view lower than the external ones, because in the
 * cockpit the disc sits across the gunsight picture. Both peak alphas are
 * uniforms the scene writes per frame (`setViewAlpha`), so the cap is one
 * documented pair of numbers rather than a second material.
 */

import * as THREE from "three/webgpu";
import type { BakedPaletteBody } from "./ramp-shade.ts";

/** Geometry attribute names the instanced writer fills — see `flight/propeller-discs.ts`. */
export const PROP_PHASE_ATTRIBUTE = "instancePropPhase";
export const PROP_RPM_ATTRIBUTE = "instancePropRpm";

/**
 * `BARON.PAL` indices for the propeller's two tones, out of the palette's own
 * wood/leather ramp (36-47, the ramp `packages/baker/src/cockpit-eye.ts`
 * identifies as a leather flying-helmet brown at 36). 38 is a dark laminated
 * walnut (117, 73, 40); 45 is the same wood catching the light (190, 130, 81),
 * used for the tip arc a spinning propeller shows and for the outer half of a
 * stopped blade.
 */
export const PROP_WOOD_INDEX = 38;
export const PROP_TIP_INDEX = 45;

/** Fraction of the radius at which the lighter tip arc starts coming in. */
const TIP_INNER_FRACTION = 0.72;
/** The rim itself is feathered over this fraction of the radius so the disc has no hard cut edge. */
const RIM_SOFT_FRACTION = 0.04;
/** Rotating lobes of banding across the disc. Three, so the fastest on-screen pattern stays well under a sixth of the display rate. */
const BANDING_LOBES = 3;
/** Peak-to-peak banding as a fraction of the disc's own alpha — a suggestion of blades, not a strobe. */
const BANDING_DEPTH = 0.45;
/** How much brighter the tip arc is than the body of the disc, as a fraction of its alpha. */
const TIP_ALPHA_GAIN = 0.6;

/** Blades on the drawn propeller. Two is what nearly every WWI scout carried. */
export const BLADE_COUNT = 2;
/** Half a blade's chord as a fraction of the disc radius — 0.21 m of chord on a 1.3 m blade. */
const BLADE_HALF_CHORD_FRACTION = 0.08;
/** Feather on the blade's edge, in the same units, so a silhouette is not aliased at range. */
const BLADE_EDGE_SOFT = 0.02;

/** The regime crossfade's edges, matching `flight/propeller-rpm.ts`'s `BLADE_REGIME_BELOW`/`DISC_REGIME_ABOVE`. */
const REGIME_LOW = 0.12;
const REGIME_HIGH = 0.3;

function paletteColor(palette: BakedPaletteBody, index: number): THREE.Node<"vec3"> {
  const entry = palette.colors[index] ?? [0, 0, 0];
  return THREE.TSL.vec3((entry[0] ?? 0) / 255, (entry[1] ?? 0) / 255, (entry[2] ?? 0) / 255);
}

/** Distance from the disc's centre in radius units, and the angle about it — both read off the interpolated local position. */
function discPolar(): { readonly radius: THREE.Node<"float">; readonly angle: THREE.Node<"float"> } {
  const local = THREE.TSL.positionGeometry;
  return { radius: THREE.TSL.length(local.xy), angle: THREE.TSL.atan(local.y, local.x) };
}

/** 0 while the propeller is near-stopped, 1 once it is spinning — the shader's half of `discRegimeMix`. */
function regimeMix(): THREE.Node<"float"> {
  return THREE.TSL.smoothstep(REGIME_LOW, REGIME_HIGH, THREE.TSL.attribute<"float">(PROP_RPM_ATTRIBUTE, "float"));
}

/** Feathers the outermost sliver of the disc so neither regime ends on a hard circle. */
function rimFade(radius: THREE.Node<"float">): THREE.Node<"float"> {
  return THREE.TSL.float(1).sub(THREE.TSL.smoothstep(1 - RIM_SOFT_FRACTION, 1, radius));
}

export interface PropDiscMaterials {
  readonly disc: THREE.MeshBasicNodeMaterial;
  readonly blades: THREE.MeshBasicNodeMaterial;
  /** Peak alpha for each regime this frame — AD-012 §4's cockpit cap lives here. */
  setViewAlpha(discAlpha: number, bladeAlpha: number): void;
  dispose(): void;
}

function translucent(material: THREE.MeshBasicNodeMaterial): THREE.MeshBasicNodeMaterial {
  material.transparent = true;
  // Depth-tested but not depth-writing: the disc is a see-through surface in
  // front of an aeroplane, and writing depth would punch its own silhouette out
  // of everything drawn after it.
  material.depthWrite = false;
  return material;
}

function discMaterial(palette: BakedPaletteBody, alpha: THREE.Node<"float">): THREE.MeshBasicNodeMaterial {
  const material = translucent(new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide }));
  const { radius, angle } = discPolar();
  const tip = THREE.TSL.smoothstep(TIP_INNER_FRACTION, 1, radius);
  material.colorNode = THREE.TSL.mix(paletteColor(palette, PROP_WOOD_INDEX), paletteColor(palette, PROP_TIP_INDEX), tip);

  const phase = THREE.TSL.attribute<"float">(PROP_PHASE_ATTRIBUTE, "float");
  const band = THREE.TSL.cos(angle.sub(phase).mul(BANDING_LOBES)).mul(BANDING_DEPTH / 2).add(1);
  material.opacityNode = alpha.mul(regimeMix()).mul(band).mul(tip.mul(TIP_ALPHA_GAIN).add(1)).mul(rimFade(radius));
  return material;
}

/**
 * Arc distance from the nearest blade centreline, in radius units: the angle
 * folded into one blade's sector, turned back into a length by the radius so a
 * blade keeps a constant chord instead of fanning out into a wedge.
 */
function bladeArcDistance(radius: THREE.Node<"float">, angle: THREE.Node<"float">): THREE.Node<"float"> {
  const sector = Math.PI / BLADE_COUNT;
  const phase = THREE.TSL.attribute<"float">(PROP_PHASE_ATTRIBUTE, "float");
  const folded = THREE.TSL.fract(angle.sub(phase).add(sector / 2).div(sector)).mul(sector).sub(sector / 2);
  return THREE.TSL.abs(THREE.TSL.sin(folded)).mul(radius);
}

function bladeMaterial(palette: BakedPaletteBody, alpha: THREE.Node<"float">): THREE.MeshBasicNodeMaterial {
  const material = translucent(new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide }));
  const { radius, angle } = discPolar();
  const tip = THREE.TSL.smoothstep(TIP_INNER_FRACTION, 1, radius);
  material.colorNode = THREE.TSL.mix(paletteColor(palette, PROP_WOOD_INDEX), paletteColor(palette, PROP_TIP_INDEX), tip);

  const onBlade = THREE.TSL.float(1).sub(
    THREE.TSL.smoothstep(BLADE_HALF_CHORD_FRACTION, BLADE_HALF_CHORD_FRACTION + BLADE_EDGE_SOFT, bladeArcDistance(radius, angle)),
  );
  material.opacityNode = alpha.mul(THREE.TSL.float(1).sub(regimeMix())).mul(onBlade).mul(rimFade(radius));
  return material;
}

/**
 * Builds the pair. `palette` is the same `BARON.PAL` body every other model
 * path reads (`flight/aircraft-mesh.ts`'s `shadingPalette`), so the propeller
 * cannot drift off the game's own colours.
 */
export function createPropDiscMaterials(palette: BakedPaletteBody): PropDiscMaterials {
  const discAlpha = THREE.TSL.uniform(0);
  const bladeAlpha = THREE.TSL.uniform(0);
  const disc = discMaterial(palette, discAlpha);
  const blades = bladeMaterial(palette, bladeAlpha);
  return {
    disc,
    blades,
    setViewAlpha(nextDiscAlpha, nextBladeAlpha) {
      discAlpha.value = nextDiscAlpha;
      bladeAlpha.value = nextBladeAlpha;
    },
    dispose() {
      disc.dispose();
      blades.dispose();
    },
  };
}
